/*
 * gtf-sticky-atc.js
 * Gym Time Fits sticky Add to Cart bar for phones (Oct 2026). Markup: snippets/gtf-sticky-atc.liquid.
 *
 * - Slides up from the bottom while the real Add to Cart button is off screen, phones only.
 * - Both buttons (the bar and the page's real Add to Cart, every screen size) read "Add to cart".
 *   Tapped before a size is picked on this visit, they add nothing: they switch to "Choose your
 *   size" and the size buttons flash (the bar also scrolls to them), so nobody gets the
 *   pre-selected Small without choosing it (Jim, Oct 18 2026).
 * - After a size is picked the bar mirrors the real button (text, sold out, busy) and presses it,
 *   so the theme's own cart code and cart drawer run exactly as before.
 * - Keeps the size chart app's "Find My Size" button next to "Size" after the theme rebuilds
 *   the size buttons on a color or size tap.
 */
(() => {
  const bar = document.querySelector('[data-gtf-sticky-atc]');
  if (!bar || bar.dataset.ready) return;
  bar.dataset.ready = 'true';

  const sectionId = bar.dataset.sectionId;
  const productInfo = document.getElementById(`MainProduct-${sectionId}`);
  if (!productInfo) return;

  const phone = window.matchMedia('(max-width: 749px)');
  const button = bar.querySelector('[data-gtf-sticky-button]');
  const label = bar.querySelector('[data-gtf-sticky-label]');
  const priceOut = bar.querySelector('[data-gtf-sticky-price]');
  const variantOut = bar.querySelector('[data-gtf-sticky-variant]');
  const chooseText = bar.dataset.labelChoose;
  const needsSize = bar.dataset.needsSize === 'true';
  const addText = () => window.variantStrings?.addToCart || 'Add to cart';
  let sizeChosen = !needsSize;
  let askedForSize = false; // someone tapped Add to Cart before picking a size
  let mainInView = false;

  const mainButton = () => document.getElementById(`ProductSubmitButton-${sectionId}`);

  // Dawn names option inputs "<Option name>-<position>" (sometimes with trailing whitespace).
  const optionName = (input) => (input.name || '').trim().replace(/-\d+$/, '').toLowerCase();

  // While Dawn swaps in a new picker the old one lingers hidden for a moment, so skip hidden ones.
  const currentPicker = () =>
    [...productInfo.querySelectorAll('variant-selects')].find((picker) => picker.style.display !== 'none');

  const fieldsetFor = (name) => {
    const picker = currentPicker();
    if (!picker) return null;
    return [...picker.querySelectorAll('fieldset')].find((fieldset) => {
      const input = fieldset.querySelector('input[type="radio"]');
      return input && optionName(input) === name;
    });
  };

  const checkedValue = (name) => {
    const fieldset = fieldsetFor(name);
    const input = fieldset && fieldset.querySelector('input[type="radio"]:checked');
    return input ? input.value : '';
  };

  // null when there is no price block at all, '' when the theme hides it (unavailable combination).
  const currentPrice = () => {
    const wrap = productInfo.querySelector(`#price-${sectionId}`);
    const price = wrap && wrap.querySelector('.price');
    if (!price) return null;
    if (wrap.classList.contains('hidden')) return '';
    const item = price.classList.contains('price--on-sale')
      ? price.querySelector('.price__sale .price-item--sale')
      : price.querySelector('.price__regular .price-item--regular');
    return item ? item.textContent.trim() : '';
  };

  // The theme rewrites the real button's label after every color or size tap, so this runs on
  // each sync. Writes only on a real difference, otherwise the observer below would loop.
  function syncMainLabel() {
    const main = mainButton();
    const text = main && main.querySelector('span');
    if (!text || !needsSize) return;
    const current = text.textContent.trim();
    if (!sizeChosen && askedForSize && !main.disabled && current !== chooseText) text.textContent = chooseText;
    if (sizeChosen && current === chooseText) text.textContent = addText();
  }

  // The Kiwi size chart app adds its "Find My Size" button to the Size label once, when the page
  // loads. The theme rebuilds the size buttons after every color or size tap, which drops it, so the
  // original button (Kiwi's click handling still attached) is moved into the new label each time.
  let sizeChartButton = null;
  function keepSizeChartButton() {
    const legend = fieldsetFor('size')?.querySelector('legend');
    if (!legend) return;
    const here = legend.querySelector('.ks-chart-container');
    if (here) {
      sizeChartButton = here;
      return;
    }
    const button = sizeChartButton || productInfo.querySelector('variant-selects .ks-chart-container');
    if (!button) return;
    legend.appendChild(button);
    sizeChartButton = button;
  }

  function sync() {
    keepSizeChartButton();

    // Until a size is tapped, no size button looks picked (CSS keys off this class).
    productInfo.classList.toggle('gtf-size-unchosen', needsSize && !sizeChosen);
    syncMainLabel();

    const price = currentPrice();
    if (price !== null) priceOut.textContent = price;

    const color = checkedValue('color');
    const size = sizeChosen ? checkedValue('size') : '';
    variantOut.textContent = [color, size].filter(Boolean).join(' / ');

    if (!sizeChosen) {
      label.textContent = askedForSize ? chooseText : addText();
      button.disabled = false;
      return;
    }

    const main = mainButton();
    const mainText = main && main.querySelector('span');
    label.textContent = mainText ? mainText.textContent.trim() : label.textContent;
    button.disabled = !main || main.disabled || main.getAttribute('aria-disabled') === 'true';
  }

  // One sync per burst of changes. A timer rather than requestAnimationFrame so the label
  // is right even before the next repaint.
  let syncQueued = false;
  const queueSync = () => {
    if (syncQueued) return;
    syncQueued = true;
    setTimeout(() => {
      syncQueued = false;
      sync();
    }, 0);
  };

  function setShown() {
    const shown = phone.matches && !mainInView;
    bar.classList.toggle('is-visible', shown);
    bar.setAttribute('aria-hidden', shown ? 'false' : 'true');
    button.tabIndex = shown ? 0 : -1;
    document.body.classList.toggle('gtf-sticky-atc-on', shown);
  }

  // Asked for after a size-less Add to Cart tap: both buttons switch to "Choose your size" and the
  // size buttons flash. Scrolls only when the sizes aren't already fully on screen.
  function askForSize() {
    askedForSize = true;
    queueSync();
    const fieldset = fieldsetFor('size');
    if (!fieldset) return;
    const box = fieldset.getBoundingClientRect();
    const barTop = bar.classList.contains('is-visible') ? bar.getBoundingClientRect().top : window.innerHeight;
    if (box.top < 0 || box.bottom > barTop) fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
    fieldset.classList.remove('gtf-size-nudge');
    void fieldset.offsetWidth; // restart the flash if tapped twice
    fieldset.classList.add('gtf-size-nudge');
    const firstOpen = fieldset.querySelector('input[type="radio"]:not(.disabled)');
    if (firstOpen) firstOpen.focus({ preventScroll: true });
  }

  button.addEventListener('click', () => {
    if (!sizeChosen) {
      askForSize();
      return;
    }
    const main = mainButton();
    if (main && !main.disabled && main.getAttribute('aria-disabled') !== 'true') main.click();
  });

  // Same rule on the page's real button. Caught on the way down to the form so the theme's
  // add-to-cart code never sees the submit until a size has been tapped. getAttribute, not
  // .id: the form has a hidden input named "id", which shadows form.id.
  document.addEventListener(
    'submit',
    (event) => {
      if (sizeChosen || event.target.getAttribute('id') !== `product-form-${sectionId}`) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      askForSize();
    },
    true
  );

  // Click as well as change: tapping the size the theme pre-selected (usually S) fires no
  // change event, and it still has to count as choosing it.
  const noteOptionTap = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('variant-selects input[type="radio"]')) return;
    if (optionName(target) === 'size') sizeChosen = true;
    queueSync();
  };
  productInfo.addEventListener('change', noteOptionTap);
  productInfo.addEventListener('click', noteOptionTap);

  // Price, picker and button state are re-rendered by the theme after every color or size tap.
  new MutationObserver(queueSync).observe(productInfo, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['disabled', 'aria-disabled'],
  });

  // Hide the bar whenever the real button is on screen above it, so there are never two.
  const watchTarget = productInfo.querySelector('product-form') || mainButton();
  if (watchTarget) {
    new IntersectionObserver(
      (entries) => {
        mainInView = entries[entries.length - 1].isIntersecting;
        setShown();
      },
      { rootMargin: `0px 0px -${bar.offsetHeight || 80}px 0px` }
    ).observe(watchTarget);
  }

  phone.addEventListener('change', setShown);
  document.body.classList.add('gtf-has-sticky-atc');
  sync();
  setShown();
})();
